import {
  OPERATORS_BY_TYPE,
  type FilterCriterion,
  type FilterFieldType,
  type FilterOperator,
  type SavedFilter,
  operatorLabel,
  operatorNeedsValue,
} from '@orbind/core'
import { useEffect, useRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { Input, Select, TagSelect, cx, type SelectOption } from './core.tsx'
import { FilterCombo } from './filter-combo.tsx'

/**
 * Filtro nomeado reutilizavel em qualquer tela do sistema.
 *
 * `FilterBar` mostra os filtros salvos como botoes arredondados (o primeiro e
 * "Todos") e, no fim da lista, um "+" que abre o construtor: escolhe-se o
 * campo da funcionalidade, o operador e o valor — com as opcoes buscadas na
 * base pelo `loadValueOptions` da tela —, adiciona-se quantos criterios
 * quiser, define-se E/OU, da-se um nome e aplica.
 *
 * A tela guarda a lista (`filters`) e aplica o filtro ativo com
 * `evaluateFilter` de @orbind/core; aqui nao ha nada especifico de Kanban,
 * demanda ou Item de Trabalho.
 */

/** Campo filtravel da funcionalidade — uma coluna da tabela daquela tela. */
export type FilterField = {
  key: string
  label: string
  type: FilterFieldType
  /** Dica exibida no campo de valor. */
  placeholder?: string
}

export type FilterBarProps = {
  fields: readonly FilterField[]
  filters: readonly SavedFilter[]
  activeFilterId: string | null
  onActivate: (filterId: string | null) => void
  onChange: (filters: SavedFilter[]) => void
  /**
   * Opcoes existentes na base para o campo — chamada a cada busca digitada.
   * Sem ela, o valor e digitado livremente.
   */
  loadValueOptions?: (fieldKey: string, query: string) => Promise<SelectOption[]> | SelectOption[]
  /** Rotulo do botao que limpa o filtro. */
  allLabel?: string
  className?: string
  /** Mostra a escolha "Botao x Lista" no construtor (SavedFilter.display). */
  allowDisplayChoice?: boolean
  /** Trava os chips (ex.: um filtro de LISTA esta aplicado no quadro). */
  disabled?: boolean
}

const newId = (prefix: string) => `${prefix}-${Math.random().toString(36).slice(2, 9)}`

const emptyDraft = (fields: readonly FilterField[]): SavedFilter => ({
  id: newId('flt'),
  name: '',
  join: 'AND',
  criteria: [
    {
      id: newId('crt'),
      field: fields[0]?.key ?? '',
      operator: OPERATORS_BY_TYPE[fields[0]?.type ?? 'text'][0]!,
      value: null,
    },
  ],
})

export function FilterBar({
  fields,
  filters,
  activeFilterId,
  onActivate,
  onChange,
  loadValueOptions,
  allLabel = 'Todos',
  className,
  allowDisplayChoice = false,
  disabled = false,
  combo = false,
  renderExtra,
}: FilterBarProps & {
  /** Seção a mais no editor do filtro (ex.: as colunas da lista) — vai para o `FilterEditor`. */
  renderExtra?: FilterEditorProps['renderExtra']
  /**
   * COMBO em vez de chips (SPEC-20261002-2015).
   *
   * A barra de chips crescia em altura com o número de filtros, e cada linha a
   * mais empurrava as colunas do quadro para baixo. O combo ocupa largura
   * constante: mostra o filtro em uso e guarda o resto na lista.
   *
   * Opcional, e não o novo padrão: este componente também desenha a barra da
   * lista de quadros, e mudar o padrão alteraria uma tela que ninguém pediu
   * para mexer.
   */
  combo?: boolean
}) {
  const [draft, setDraft] = useState<SavedFilter | null>(null)
  /** id do filtro em edicao; null = criando um novo. */
  const [editingId, setEditingId] = useState<string | null>(null)
  const barRef = useRef<HTMLDivElement>(null)

  // Esc fecha a janela; o corpo nao rola atras dela
  useEffect(() => {
    if (draft === null) return
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setDraft(null)
    }
    document.addEventListener('keydown', onKeyDown)
    const overflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKeyDown)
      document.body.style.overflow = overflow
    }
  }, [draft])

  const openNew = () => {
    setEditingId(null)
    setDraft(emptyDraft(fields))
  }
  const openEdit = (filter: SavedFilter) => {
    setEditingId(filter.id)
    setDraft(structuredClone(filter))
  }

  const apply = () => {
    if (draft === null || draft.name.trim() === '') return
    const saved: SavedFilter = { ...draft, name: draft.name.trim() }
    onChange(editingId ? filters.map((f) => (f.id === editingId ? saved : f)) : [...filters, saved])
    onActivate(saved.id)
    setDraft(null)
  }

  const removeFilter = () => {
    if (editingId === null) return
    onChange(filters.filter((f) => f.id !== editingId))
    if (activeFilterId === editingId) onActivate(null)
    setDraft(null)
  }


  const ativo = filters.find((f) => f.id === activeFilterId) ?? null

  return (
    <div className={cx('ob-filterbar', combo && 'ob-filterbar--combo', className, disabled && 'ob-filterbar--off')} ref={barRef}>
      {combo ? (
        <FilterCombo
          options={filters.map((f) => ({ id: f.id, name: f.name }))}
          activeId={activeFilterId}
          onActivate={onActivate}
          onEdit={(option) => {
            const filtro = filters.find((f) => f.id === option.id)
            if (filtro) openEdit(filtro)
          }}
          onRemove={(option) => {
            onChange(filters.filter((f) => f.id !== option.id))
            // remover o que estava em uso volta para "todos": deixar o id morto
            // ativo esconderia cartões sem nada explicar
            if (activeFilterId === option.id) onActivate(null)
          }}
          onNew={openNew}
          allLabel={allLabel}
          disabled={disabled}
        />
      ) : (
        <>
      {filters.length > 0 ? (
        <button
          type="button"
          className={cx('ob-filterchip', activeFilterId === null && 'ob-filterchip--on')}
          onClick={() => onActivate(null)}
        >
          {allLabel}
        </button>
      ) : null}

      {filters.map((filter) => (
        <span
          key={filter.id}
          className={cx('ob-filterchip', activeFilterId === filter.id && 'ob-filterchip--on')}
        >
          <button type="button" className="ob-filterchip__label" onClick={() => onActivate(filter.id)}>
            {filter.name}
          </button>
          <button
            type="button"
            className="ob-filterchip__edit"
            aria-label={`Editar filtro ${filter.name}`}
            title="Editar filtro"
            onClick={() => openEdit(filter)}
          >
            <i className="fas fa-pen" aria-hidden />
          </button>
        </span>
      ))}

      <button type="button" className="ob-filterchip ob-filterchip--add" aria-label="Novo filtro" title="Novo filtro" onClick={openNew}>
        <i className="fas fa-plus" aria-hidden />
      </button>

        </>
      )}

      {draft !== null
        ? createPortal(
            <>
              <div className="ob-filtermodal__backdrop" onClick={() => setDraft(null)} aria-hidden />
              <div className="ob-filtermodal" role="dialog" aria-modal="true" aria-label={editingId ? 'Editar filtro' : 'Novo filtro'}>
              <div className="ob-filterpop__head">
                <span className="ob-filterpop__title">{editingId ? 'Editar filtro' : 'Novo filtro'}</span>
                <button type="button" className="ob-filterpop__close" aria-label="Fechar" onClick={() => setDraft(null)}>
                  <i className="fas fa-times" aria-hidden />
                </button>
              </div>

              <FilterEditor
                fields={fields}
                value={draft}
                onChange={setDraft}
                loadValueOptions={loadValueOptions}
                onSubmit={apply}
                showName
                showDisplayChoice={allowDisplayChoice}
                renderExtra={renderExtra}
              />

              <div className="ob-filterpop__footer">
                {editingId ? (
                  <button type="button" className="ob-filterpop__delete" onClick={removeFilter}>
                    Excluir
                  </button>
                ) : null}
                <span className="ob-grow" />
                <button type="button" className="ob-filterpop__cancel" onClick={() => setDraft(null)}>
                  Cancelar
                </button>
                <button type="button" className="ob-filterpop__apply" disabled={draft.name.trim() === ''} onClick={apply}>
                  Aplicar
                </button>
              </div>
              </div>
            </>,
            document.body,
          )
        : null}
    </div>
  )
}

export type FilterEditorProps = {
  fields: readonly FilterField[]
  value: SavedFilter
  onChange: (filter: SavedFilter) => void
  loadValueOptions?: FilterBarProps['loadValueOptions']
  /** Enter no nome dispara isto (o modal usa para aplicar). */
  onSubmit?: () => void
  /** Exibe o campo "Nome do filtro" — embutido em formularios costuma ficar off. */
  showName?: boolean
  namePlaceholder?: string
  /** Permite remover o ULTIMO critério (fica sem nenhum). Util para regras
   *  opcionais, onde "sem critério" significa "sempre". Filtros deixam off. */
  allowEmpty?: boolean
  /** Escolha "Botao x Lista" (SavedFilter.display) — o Kanban usa. */
  showDisplayChoice?: boolean
  /**
   * Seção a mais no fim do editor, que lê e grava o mesmo rascunho — a lista
   * de Itens põe aqui as colunas do filtro (SPEC-20261103-1217).
   */
  renderExtra?: (draft: SavedFilter, setDraft: (filter: SavedFilter) => void) => ReactNode
}

/**
 * Miolo do construtor: criterios (campo + operador + valor), E/OU e nome.
 *
 * Fica separado do `FilterBar` para poder ser embutido em qualquer formulario
 * — por exemplo, na aba "Filtro" da configuracao de um quadro.
 */
export function FilterEditor({
  fields,
  value: draft,
  onChange: setDraft,
  loadValueOptions,
  onSubmit,
  showName = false,
  namePlaceholder = 'Ex.: Meus quadros ativos',
  allowEmpty = false,
  showDisplayChoice = false,
  renderExtra,
}: FilterEditorProps) {
  const patchCriterion = (criterionId: string, patch: Partial<FilterCriterion>) =>
    setDraft({ ...draft, criteria: draft.criteria.map((c) => (c.id === criterionId ? { ...c, ...patch } : c)) })

  const addCriterion = () =>
    setDraft({
      ...draft,
      criteria: [
        ...draft.criteria,
        {
          id: newId('crt'),
          field: fields[0]?.key ?? '',
          operator: OPERATORS_BY_TYPE[fields[0]?.type ?? 'text'][0]!,
          value: null,
        },
      ],
    })

  return (
    <div className="ob-filterpop__body">
      {/* o nome vem PRIMEIRO: e o que identifica o filtro na barra */}
      {showName ? (
        <div className="ob-filtername ob-filtername--first">
          <label className="ob-filterpop__namelabel" htmlFor={`${draft.id}-name`}>
            Nome do filtro
          </label>
          <Input
            id={`${draft.id}-name`}
            autoFocus
            value={draft.name}
            placeholder={namePlaceholder}
            onChange={(e) => setDraft({ ...draft, name: e.target.value })}
            onKeyDown={(e) => {
              if (e.key === 'Enter') onSubmit?.()
            }}
          />
        </div>
      ) : null}

      {/* onde o filtro aparece: chip na barra (botao) ou so na lista da busca */}
      {showDisplayChoice ? (
        <div className="ob-filtername">
          <span className="ob-filterpop__namelabel">Onde o filtro aparece</span>
          <div className="ob-filterjoin ob-filterjoin--display">
            <button
              type="button"
              className={cx('ob-filterjoin__opt', (draft.display ?? 'button') === 'button' && 'ob-filterjoin__opt--on')}
              onClick={() => setDraft({ ...draft, display: 'button' })}
            >
              <i className="fas fa-grip-lines" aria-hidden /> Botão na barra
            </button>
            <button
              type="button"
              className={cx('ob-filterjoin__opt', draft.display === 'list' && 'ob-filterjoin__opt--on')}
              onClick={() => setDraft({ ...draft, display: 'list' })}
            >
              <i className="fas fa-list" aria-hidden /> Lista da busca
            </button>
          </div>
        </div>
      ) : null}

      {draft.criteria.map((criterion, index) => (
        <div className="ob-filterrow" key={criterion.id}>
          {index > 0 ? (
            <div className="ob-filterjoin">
              <button
                type="button"
                className={cx('ob-filterjoin__opt', draft.join === 'AND' && 'ob-filterjoin__opt--on')}
                onClick={() => setDraft({ ...draft, join: 'AND' })}
              >
                E
              </button>
              <button
                type="button"
                className={cx('ob-filterjoin__opt', draft.join === 'OR' && 'ob-filterjoin__opt--on')}
                onClick={() => setDraft({ ...draft, join: 'OR' })}
              >
                OU
              </button>
            </div>
          ) : null}

          <div className="ob-filterrow__card">
            <div className="ob-filterrow__grid">
              <div className="ob-filterfield">
                <span className="ob-filterfield__label">Campo</span>
                <Select
                  value={criterion.field}
                  placeholder="Escolha o campo"
                  options={fields.map((f) => ({ value: f.key, label: f.label }))}
                  onChange={(fieldKey) => {
                    const field = fields.find((f) => f.key === fieldKey)
                    patchCriterion(criterion.id, {
                      field: fieldKey,
                      operator: OPERATORS_BY_TYPE[field?.type ?? 'text'][0]!,
                      value: null,
                    })
                  }}
                />
              </div>
              <div className="ob-filterfield">
                <span className="ob-filterfield__label">Operador</span>
                <Select
                  value={criterion.operator}
                  placeholder="Operador"
                  options={(OPERATORS_BY_TYPE[fields.find((f) => f.key === criterion.field)?.type ?? 'text'] ?? []).map(
                    (op) => ({ value: op, label: operatorLabel(op) }),
                  )}
                  onChange={(operator) => patchCriterion(criterion.id, { operator: operator as FilterOperator, value: null })}
                />
              </div>
              <div className="ob-filterfield">
                <span className="ob-filterfield__label">Valor</span>
                <CriterionValue
                  field={fields.find((f) => f.key === criterion.field)}
                  criterion={criterion}
                  loadValueOptions={loadValueOptions}
                  onChange={(v) => patchCriterion(criterion.id, { value: v })}
                />
              </div>
            </div>
            {draft.criteria.length > 1 || allowEmpty ? (
              <button
                type="button"
                className="ob-filterrow__remove"
                aria-label="Remover critério"
                title="Remover critério"
                onClick={() => setDraft({ ...draft, criteria: draft.criteria.filter((c) => c.id !== criterion.id) })}
              >
                <i className="fas fa-times" aria-hidden />
              </button>
            ) : null}
          </div>
        </div>
      ))}

      <button type="button" className="ob-filterpop__add" onClick={addCriterion}>
        <i className="fas fa-plus" aria-hidden />
        Adicionar critério
      </button>

      {renderExtra ? renderExtra(draft, setDraft) : null}
    </div>
  )
}

/** Filtro vazio pronto para edicao (util para embutir o FilterEditor). */
export function emptyFilter(fields: readonly FilterField[], name = ''): SavedFilter {
  return { ...emptyDraft(fields), name }
}

/** Campo de valor do criterio — muda conforme o tipo e o operador. */
function CriterionValue({
  field,
  criterion,
  loadValueOptions,
  onChange,
}: {
  field: FilterField | undefined
  criterion: FilterCriterion
  loadValueOptions?: FilterBarProps['loadValueOptions']
  onChange: (value: string | string[] | null) => void
}) {
  const [options, setOptions] = useState<SelectOption[]>([])
  const type = field?.type ?? 'text'
  const wantsOptions = Boolean(loadValueOptions) && (type === 'select' || type === 'boolean' || type === 'text')

  useEffect(() => {
    if (!field || !loadValueOptions || !wantsOptions) return
    let cancelled = false
    Promise.resolve(loadValueOptions(field.key, '')).then((loaded) => {
      if (!cancelled) setOptions(loaded)
    })
    return () => {
      cancelled = true
    }
  }, [field, loadValueOptions, wantsOptions])

  if (!operatorNeedsValue(criterion.operator)) {
    return <span className="ob-filtervalue__none">sem valor</span>
  }

  if (criterion.operator === 'between') {
    const pair = Array.isArray(criterion.value) ? criterion.value : ['', '']
    return (
      <div className="ob-filtervalue__pair">
        <Input
          value={pair[0] ?? ''}
          placeholder="de"
          onChange={(e) => onChange([e.target.value, pair[1] ?? ''])}
        />
        <Input
          value={pair[1] ?? ''}
          placeholder="até"
          onChange={(e) => onChange([pair[0] ?? '', e.target.value])}
        />
      </div>
    )
  }

  // multi-valor: as opcoes vem da base e viram tags
  if (criterion.operator === 'in' || criterion.operator === 'not_in') {
    return (
      <TagSelect
        value={Array.isArray(criterion.value) ? criterion.value : criterion.value ? [criterion.value] : []}
        options={options}
        placeholder={field?.placeholder ?? 'Buscar valores…'}
        onChange={(values) => onChange(values)}
      />
    )
  }

  if (wantsOptions && options.length > 0 && type !== 'text') {
    return (
      <Select
        value={typeof criterion.value === 'string' ? criterion.value : null}
        placeholder={field?.placeholder ?? 'Valor'}
        options={options}
        onChange={(value) => onChange(value)}
      />
    )
  }

  // texto livre com sugestoes do que existe na base
  return (
    <>
      <Input
        value={typeof criterion.value === 'string' ? criterion.value : ''}
        type={type === 'number' ? 'number' : 'text'}
        placeholder={field?.placeholder ?? 'Valor'}
        list={options.length > 0 ? `${criterion.id}-opts` : undefined}
        onChange={(e) => onChange(e.target.value)}
      />
      {options.length > 0 ? (
        <datalist id={`${criterion.id}-opts`}>
          {options.map((option) => (
            <option key={option.value} value={option.label} />
          ))}
        </datalist>
      ) : null}
    </>
  )
}
